/* eslint-disable max-lines, complexity */
import { SelectableGroup } from '@cloud/react-selectable';
import { EditorID } from 'Cloud/Application/Editor/types';
import classNames from 'clsx';
import store from 'lib/store';
import { isNil, path } from 'ramda';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import { EDITORS_CONFIG, USER_EMAIL } from 'reactApp/appHelpers/configHelpers';
import { abSafeFakeDoorSelector } from 'reactApp/appHelpers/featuresHelpers';
import { mediaFileTypes } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoImproving';
import { lastFilesWidgetHidden } from 'reactApp/appHelpers/featuresHelpers/features/lastFilesWidget';
import { enableNewPersonalDocuments } from 'reactApp/appHelpers/featuresHelpers/features/newPersonalDocuments';
import { isScrollTopBtnEnabled } from 'reactApp/appHelpers/featuresHelpers/features/scrollTopBtn';
import { showSharedFileAuthor } from 'reactApp/appHelpers/featuresHelpers/features/showSharedFileAuthor';
import { chooseVariant } from 'reactApp/appHelpers/featuresHelpers/utils';
import { QuotaCleanerPlug } from 'reactApp/components/QuotaCleanerPlug/QuotaCleanerPlug';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useFamilyShowFeaturesHook, useShowFamilyDocumentsHook } from 'reactApp/hooks/useFamilyShowHook';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { usePortal } from 'reactApp/hooks/usePortal';
import { getAllDocumentsCategory, getAllDocumentsCurrentType } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { AllDocumentsCategoryType, EAllDocumentsType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { getBizCurrentCategory } from 'reactApp/modules/bizCategories/bizCategories.selectors';
import { BizCategoryType } from 'reactApp/modules/bizCategories/bizCategories.types';
import { isDialogVisible } from 'reactApp/modules/dialog/dialog.selectors';
import { getDownloadEbookClick } from 'reactApp/modules/ebook/ebook.selectors';
import {
    getFeatureEditingNonEditableFormatsData,
    getFeatureHideCreateUploadInRoFolders,
    getFeatureShowStories,
} from 'reactApp/modules/features/features.selectors';
import { getFeedCategory, getLastFilesForWidget } from 'reactApp/modules/feed/feed.selectors';
import { isMediaFolder, isMediaFolderAndGalleryEnabled, isVirusItem } from 'reactApp/modules/file/utils';
import { getGalleryCategory } from 'reactApp/modules/gallery/gallery.selectors';
import { GalleryCategoryType } from 'reactApp/modules/gallery/gallery.types';
import { setLastOpenFolder } from 'reactApp/modules/home/home.actions';
import {
    getCurrentFolderHome,
    getDomainFoldersFilterActive,
    getIsRedirectFromViewer,
    getLastOpenFolder,
    isReadOnly,
    isThisOrParentMounted,
} from 'reactApp/modules/home/home.selectors';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { EpubPromoType } from 'reactApp/modules/promo/sagas/epub.saga';
import { getCurrentPublicFolder, isPublicDownloadableEnabled, isPublicUploadEnabled } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId, getCurrentStorage, getParams } from 'reactApp/modules/router/router.selectors';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { scrollToItemAction } from 'reactApp/modules/storage/storage.module';
import {
    getParentMountedOrSharedItems,
    getSelectedItem,
    getStorageCurrentFolder,
    getStorageItemById,
    groupedIds,
    hasLoadMore,
} from 'reactApp/modules/storage/storage.selectors';
import { type CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { getStorySummaryCovers } from 'reactApp/modules/stories/stories.selectors';
import { isProgressingStatus, showUploadButton } from 'reactApp/modules/uploadList/uploadList.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { SelectFromCloudEmpty } from 'reactApp/sections/AlbumsPage/SelectFromCloudPopup/components/SelectFromCloudEmpty/SelectFromCloudEmpty';
import { getParams as getAttachesParams } from 'reactApp/sections/AttachesPage/helpers';
import type { RootState } from 'reactApp/store';
import {
    DATA_LIST_BREADCRUMBS_ID,
    DATA_LIST_EXTRABLOCKS_ID,
    DATA_LIST_GALLERY_TAB_MENU_ID,
    DATA_LIST_LAST_FILES_WIDGET_ID,
    DATA_LIST_PUBLIC_UPLOAD_HEADER_ID,
    DATA_LIST_STORIES_WIDGET_ID,
    FAMILY_DOCUMENTS_FAKEDOOR_ID,
    SAFE_FAKEDOOR_ID,
} from 'reactApp/ui/Datalist/DataList.ids';
import { WrapDataListItem } from 'reactApp/ui/Datalist/Item/DataListItem';
import type { IProvocation } from 'reactApp/ui/Datalist/UploadProvocation/UploadProvocation.types';
import { useHandleCallback } from 'reactApp/ui/Datalist/UserActions/handleCallback';
import { useHandleDragAndDropCallback } from 'reactApp/ui/Datalist/UserActions/handleDragAndDropCallback';
import { useHandleMouseCallback } from 'reactApp/ui/Datalist/UserActions/handleMouseCallback';
import { useHotkeyHandlers } from 'reactApp/ui/Datalist/UserActions/hotkeys';
import { onAnchorScroll } from 'reactApp/ui/Datalist/UserActions/onAnchorScrollAction';
import { onScroll } from 'reactApp/ui/Datalist/UserActions/onScrollAction';
import { DataListDragItem } from 'reactApp/ui/DataListDragItem/DataListDragItem';
import { getThumbUrl } from 'reactApp/ui/DataListItem/DataListItem.helpers';
import { ID_NEW_ITEM } from 'reactApp/ui/DataListItemCreateNew/DataListItemCreateNew';
import { Empty } from 'reactApp/ui/Empty/Empty';
import { EmptyFolder } from 'reactApp/ui/EmptyFolder/EmptyFolder';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { LastFilesWidget } from 'reactApp/ui/LastFilesWidget/LastFilesWidget';
import { Loader } from 'reactApp/ui/Loader/Loader';
import { PublicUploadHeader } from 'reactApp/ui/PublicUploadHeader/PublicUploadHeader';
import { ScrollTopButton } from 'reactApp/ui/ScrollTopButton/ScrollTopButton';
import { EmptySearch } from 'reactApp/ui/SearchMessage/EmptySearch/EmptySearch';
import { ErrorSearch } from 'reactApp/ui/SearchMessage/ErrorSearch/ErrorSearch';
import { VirtualList as VirtualListNew } from 'reactApp/ui/VirtualList/VirtualList.new';
import type { IVirtualList } from 'reactApp/ui/VirtualList/VirtualList.types';
import type { ESearchOptionType } from 'reactApp/ui/WebSearch/WebSearch.data';
import { getIsCanShowAiTooltipOnMediaFiles } from 'reactApp/utils/aiPhotoImproving';
import { getPublicUrl } from 'reactApp/utils/urlHelper';

import { BreadcrumbsContainer } from '../BreadcrumbsContainer/BreadcrumbsContainer';
import { ErrorRetry } from '../ErrorRetry/ErrorRetry';
import { FamilyContentEmpty } from '../FamilyContentEmpty/FamilyContentEmpty';
import { HOT_KEY_SCOPE_NAME, ROW_HEIGHT } from './DataList.constants';
import styles from './DataList.css';
import { getShowPromoTip } from './DataList.helpers';
import {
    useAutoSwitchFromGallery,
    useGaSelectSend,
    useGaShowGallery,
    useHandleMouse,
    useHandlerOnDocumentClick,
    useHandleStopDrag,
    useLoadToItem,
    useLogHandler,
    useScrollToItem,
    useSendDwh,
    useSendGaStat,
    useSetActiveIndex,
    useSetInitialViewerId,
    useShowAiIconAndTooltip,
} from './DataList.hooks';
import type { Props } from './DataList.types';
import { DataListItemPlug } from './DataListItemPlug/DataListItemPlug';
import { ExtraBlocks } from './ExtraBlocks/ExtraBlocks';
import { UploadProvocation } from './UploadProvocation/UploadProvocation';

export const DataList = memo(
    // eslint-disable-next-line max-lines-per-function
    ({
        storage,
        viewMode: currentViewMode,
        disableCenteredLoader = false,
        limitItemsNumber = 0,
        disableTips = false,
        gaSuffix = '',
        initialItemId = '',
        disableLoadOnScroll = false,
        isPopup = false,
        itemsList,
        customScrollElement,
        onItemSelectCallback,
        withinDialog,
        storiesWidget,
        albumShowSelected = false,
        isLastFiles,
        selectBoxEnabled = true,
        selectableClassName,
        showBreadcrumbs = true,
        showExtraBlocks = true,
        isFastPage,
        isFastScrollbar,
    }: Props): ReactElement => {
        const startTime = Date.now();
        const dragParams = useRef<{
            dragItem: null | CloudItem;
            startX: number;
            startY: number;
            isDragging: boolean;
        }>({
            dragItem: null,
            startX: 0,
            startY: 0,
            isDragging: false,
        });

        const dispatch = useDispatch();
        const isDragging = useSelector(SelectionsSelectors.isDragging);
        const [dragPosX, setDragPosX] = useState(0);
        const [dragPosY, setDragPosY] = useState(0);
        const [tipShown, setTipShown] = useState(false);
        const [dragOverItem, setDragOverItem] = useState<CloudItem | null>(null);
        const [selectingIdxs, setSelectingIdxs] = useState<string[]>([]);
        const [initialViewerId, setInitialViewerId] = useState<string>(initialItemId);
        const prevSelectHandler = useRef(null);
        const registerItem = useRef<null | any>(null);
        const unregisterItem = useRef<null | any>(null);
        const listRef = useRef<IVirtualList | null>(null);
        const itemCreateNewRef = useRef<HTMLDivElement | null>(null);
        const provocationRef = useRef<IProvocation | null>(null);
        const routerParams = useSelector(getParams);
        const currentAllDocsType = useSelector(getAllDocumentsCurrentType);
        const currentAllDocsCategory = useSelector(getAllDocumentsCategory);
        const currentStorage = useSelector(getCurrentStorage) as EStorageType;
        const domainFoldersFilterActive = useSelector(getDomainFoldersFilterActive);
        const bizCurrentCategory = useSelector(getBizCurrentCategory);
        const emptyBizCategory = !bizCurrentCategory || bizCurrentCategory === BizCategoryType.all;
        const hideCreateUploadInRoFolders = useSelector(getFeatureHideCreateUploadInRoFolders);
        const featureFormatsData = useSelector(getFeatureEditingNonEditableFormatsData);
        const redirectFromViewerItem = useSelector(getIsRedirectFromViewer);
        const isR7Editor = EDITORS_CONFIG[0]?.id === EditorID.R7_WOPI || EDITORS_CONFIG[0]?.id === EditorID.R7;
        const canCreatePresentation =
            isR7Editor &&
            EDITORS_CONFIG[0]?.edit_extensions?.includes?.('pptx') &&
            EDITORS_CONFIG[0]?.create_extensions?.includes?.('pptx');

        const lastOpenFolder = useSelector(getLastOpenFolder);
        const currentRoute = useSelector(getCurrentRouteId);

        storage = storage || currentStorage;
        const {
            isHome,
            isPublic,
            isIncomingPublic,
            isSharedLinks,
            isSearch,
            isAttaches,
            isSharedAutodelete,
            isAlbums,
            isQuotaCleaner,
            isAllDocuments,
            isFeed,
            isGallery,
            isStock,
            isFamilyFolders,
            isTrashBin,
        } = getStorage(storage);

        const currentCategory = useSelector(getAllDocumentsCategory);

        const folder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage as EStorageType));
        const { isDomainAndCreateRestricted, isDomainAndShareRestricted, isDomainAndDeleteRestricted } =
            getDomainRestrictionsForItems(folder);
        const readonly = useSelector((state: RootState) => isReadOnly(state, folder?.id));
        const isReadOnlyFolder = readonly || isDomainAndCreateRestricted;
        const canDisplayEmptyStubByRights = !readonly && !(isDomainAndShareRestricted && isDomainAndCreateRestricted);

        const attachesParams = getAttachesParams({ params: useSelector(SettingsSelectors.getQueryParams) });
        let items = useSelector((state: RootState) =>
            itemsList?.length ? itemsList : groupedIds(state, storage as EStorageType, false, isLastFiles, isPopup)
        ) as string[];
        const isBizUser = useSelector(UserSelectors.isBizUser);
        const isB2bBizUser = useSelector(UserSelectors.isB2BUser);
        const storeStories = useSelector(getStorySummaryCovers);
        const lastFiles = useSelector(getLastFilesForWidget);
        const userHasStories = storeStories?.length > 0;
        const isShowStoryEnabled = useSelector(getFeatureShowStories);
        const item = useSelector(getCurrentFolderHome);
        const { id, isFolder } = item || {};
        const isRoot = isFolder && id === ROOT_FOLDER_ID;

        const { type: searchRequestType, query: searchRequestQuery } = useSelector(getSearchRequestParams);

        const showNewItemInAllDocs =
            isAllDocuments &&
            currentAllDocsType &&
            ([EAllDocumentsType.document, EAllDocumentsType.spreadsheet].includes(currentAllDocsType) ||
                (canCreatePresentation && currentAllDocsType === EAllDocumentsType.presentation));

        const hasExtraBlocksItem = !isQuotaCleaner && !isPopup && !isLastFiles && showExtraBlocks;
        const hasBreadCrumbsItem = (isHome || isPublic || isStock) && showBreadcrumbs;

        const hasStoriesWidgetItem = isShowStoryEnabled && userHasStories && !isPopup && isGallery;

        const hasLastFilesWidgetItem =
            isRoot && isShowStoryEnabled && isHome && lastFiles?.length > 0 && !lastFilesWidgetHidden && !isFastPage;

        const isDocumentCategoryWithoutAdding =
            currentCategory === AllDocumentsCategoryType.sharedWithYou || currentCategory === AllDocumentsCategoryType.youShared;

        const isSharedCategory = currentCategory === AllDocumentsCategoryType.sharedWithYou || isIncomingPublic || isPublic;

        const hasAddedItem = Boolean(
            (((isHome || showNewItemInAllDocs || isFamilyFolders) &&
                !domainFoldersFilterActive &&
                (folder?.childs?.length || folder?.id === ROOT_FOLDER_ID)) ||
                (isSharedAutodelete && items.length) ||
                (isSharedLinks && items.length)) &&
                !isDocumentCategoryWithoutAdding
        );

        const storageViewMode = useSelector((state) =>
            SettingsSelectors.getViewByStorage(state, storage, isMediaFolderAndGalleryEnabled(state, folder, storage))
        );
        const viewMode = currentViewMode || storageViewMode;

        if (limitItemsNumber > 0) {
            items = items.slice(0, limitItemsNumber);
        }

        const showFamilyDocuments = useShowFamilyDocumentsHook();
        const isFamilyTariff = useFamilyShowFeaturesHook();

        if (hasAddedItem && !(isBizUser && isReadOnlyFolder && hideCreateUploadInRoFolders) && viewMode !== EViewMode.gallery) {
            if (
                chooseVariant(
                    abSafeFakeDoorSelector,
                    {
                        control: false,
                        variant1: storage === EStorageType.home && isRoot,
                    },
                    {
                        skipCondition: {
                            variant2: true,
                        },
                    }
                )
            ) {
                items = [ID_NEW_ITEM, SAFE_FAKEDOOR_ID, ...items];
            } else {
                items = [ID_NEW_ITEM, ...items];
            }

            const familyDocumentsShownPreview = isFamilyTariff || !store.get(`${USER_EMAIL}|family-documents-datalist-item`);

            if (
                showFamilyDocuments &&
                ((storage === EStorageType.home && isRoot) || storage === EStorageType.familyFolders) &&
                !items.find((item) => item.match(/Документы семьи/)) &&
                familyDocumentsShownPreview
            ) {
                items = [ID_NEW_ITEM, FAMILY_DOCUMENTS_FAKEDOOR_ID, ...items.slice(1)];
            }
        }

        const isMounted = useSelector((state: RootState) => isThisOrParentMounted(state, folder?.id));

        const selectedIdxs = useSelector(SelectionsSelectors.getSelectedIdxs);
        const selectedCount = selectedIdxs.length;
        const selectedItems = useSelector((state: RootState) =>
            selectedIdxs.map((id) => getStorageItemById(state, storage as EStorageType, id)).filter((item) => !!item)
        );

        const folderId = folder?.id || '';
        const isLoading = folder?.isLoading;
        const isLoaded = folder?.isLoaded;
        const error = path(['error'], folder);
        const hasMoreToLoad = useSelector((state: RootState) => hasLoadMore(state, storage, folder));

        const isFavoritesEnabled = (isHome || isSearch || isAlbums) && !isAllDocuments && !isMounted;
        const draggingPortalTarget = usePortal('draggingElement');
        const isSearchActive = isSearch || (isAttaches && attachesParams?.query);
        const isModalOpen: boolean = useSelector(isDialogVisible);
        const isPublicUpload = useSelector(isPublicUploadEnabled) && !isDomainAndCreateRestricted;
        const isPublicDownloaded = useSelector(isPublicDownloadableEnabled);
        const publicFolder = useSelector(getCurrentPublicFolder);
        const galleryCategory = useSelector(getGalleryCategory);
        const feedCategory = useSelector(getFeedCategory);
        const cloudItems = useSelector((state: RootState) =>
            items.map((id) => getStorageItemById(state, storage as EStorageType, id)).filter((item) => !!item)
        ) as CloudItem[];
        const listOfBannedToFavoriteItems = useSelector((state: RootState) => getParentMountedOrSharedItems(state, cloudItems));

        const secondClickPromo = useSelector(PromoSelectors.pullPromo(EPromoType.secondClick));
        const epubPromo = useSelector(PromoSelectors.pullPromo(EPromoType.epubPromo));
        const isDownloadEbookClick = useSelector(getDownloadEbookClick);
        const isUploading = useSelector(isProgressingStatus);
        const isUploadButtonVisible = useSelector((state: RootState) => showUploadButton(state, undefined));
        const hideRestore = isTrashBin && isDomainAndDeleteRestricted;

        const promo = isDownloadEbookClick && epubPromo?.tipType?.includes(EpubPromoType.downloadList) ? epubPromo : secondClickPromo;

        const tipData = getShowPromoTip({
            selectedCount,
            isDragging,
            disableTips,
            promo,
            selectedItems,
        });

        useEffect(() => {
            if (promo && tipData && !tipShown) {
                promo.onShow();
                setTipShown(true);
            }
            if (promo && !tipData && tipShown && 'onHide' in promo) {
                promo.onHide();
            }
            // Если после показа тултипа
            // сняли выделение с элемента или кликнули по нему, то считаем, что его закрыли (без клика по крестику)
            if (tipShown && !tipData && promo) {
                setTipShown(false);
                promo.onClose();
            }
        }, [promo, tipData, tipShown]);

        const { handleStartDrag, handleStopDrag, handleDraggingOver } = useHandleDragAndDropCallback({
            dragParams,
            selectedIdxs,
            dispatch,
            storage,
            setDragOverItem,
            setDragPosX,
            setDragPosY,
            prevSelectHandler,
            dragOverItem,
            selectedItems,
            isDragging,
        });

        const { handleMouseMove, handleMouseUp, handleMouseDownOnItem, handleMouseLeave } = useHandleMouseCallback({
            dragParams,
            storage,
            setDragOverItem,
            setDragPosX,
            setDragPosY,
            isDragging,
            handleStartDrag,
        });

        const filteredItems = useMemo(
            () =>
                items.filter(
                    (block) =>
                        ![
                            ID_NEW_ITEM,
                            SAFE_FAKEDOOR_ID,
                            DATA_LIST_BREADCRUMBS_ID,
                            DATA_LIST_EXTRABLOCKS_ID,
                            DATA_LIST_STORIES_WIDGET_ID,
                            DATA_LIST_LAST_FILES_WIDGET_ID,
                            DATA_LIST_GALLERY_TAB_MENU_ID,
                            DATA_LIST_PUBLIC_UPLOAD_HEADER_ID,
                            FAMILY_DOCUMENTS_FAKEDOOR_ID,
                        ].includes(block)
                ),
            [items?.length]
        );

        // CLOUDWEB-19872 Фейкдор "Поиск и организация пространства для файлов в Облаке" ч.2
        const isShowAiIconAndTooltip = useShowAiIconAndTooltip(storage);

        // CLOUDWEB-19949 - улучшение качества фото
        const isShowAiPhotoImprovingTooltip = useMemo(() => {
            return getIsCanShowAiTooltipOnMediaFiles();
        }, [storage]);

        const itemsWithNewElementAndNoOtherFake = filteredItems.length + (hasAddedItem ? 1 : 0);

        const loadOnScroll = useCallback(() => {
            onScroll({
                storage,
                items: filteredItems,
                folderId,
                hasMoreToLoad,
                dispatch,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [disableLoadOnScroll, folderId, items.length, dispatch, hasAddedItem, hasMoreToLoad]);

        /** Я че зря bind учил */
        const loadOnAnchor = useCallback(onAnchorScroll.bind(null, dispatch), [dispatch]);

        const [activeIndex, isElementLoaded, setActiveIndex] = useLoadToItem(items, loadOnScroll, isLoading);

        const selectedItem = useSelector(getSelectedItem);

        const { type: attachType, folderId: attachFolderId } = useSelector(SettingsSelectors.getQueryParams);

        useScrollToItem({
            activeIndex,
            dispatch,
            listRef,
            isElementLoaded: isElementLoaded && !isLoading && isLoaded,
        });

        useSetActiveIndex({ selectedItem, items, setActiveIndex, storage });

        useSetInitialViewerId({
            isLoaded,
            loadOnScroll,
            initialViewerId,
            setInitialViewerId,
            items,
            storage,
            dispatch,
        });

        useHandleStopDrag({ isDragging, handleStopDrag, selectedItems, storage });

        useHandleMouse({ handleMouseMove, handleMouseUp });

        useAutoSwitchFromGallery({ viewMode, folderId: folder?.id, isMediaFolder: isMediaFolder(folder), storage });

        const {
            handleOnDocumentClick,
            handleOnSelectGroupClick,
            handleBeginSelection,
            handleOnContextMenu,
            handleOnContextMenuItem,
            handleOnAuthorContextMenuItem,
            handleOnSelect,
            handleSelectboxSelection,
            handleSelecting,
            handleOnClick,
            handleClickCreateNew,
        } = useHandleCallback({
            isModalOpen,
            setActiveIndex,
            dispatch,
            selectedItem,
            prevSelectHandler,
            storage,
            gaSuffix,
            folder,
            items,
            selectedIdxs,
            selectingIdxs,
            setSelectingIdxs,
            routerParams,
            itemCreateNewRef,
            viewMode,
            isPopup,
            onItemSelectCallback,
            attachType,
            attachFolderId,
            currentAllDocsType,
            featureFormatsData,
            currentCategory,
        });

        useHandlerOnDocumentClick({ handleOnDocumentClick });

        useLogHandler({ items, viewMode, startTime, folder });

        useGaSelectSend({ selectedCount });

        useGaShowGallery({ viewMode });

        useSendGaStat({ folder });

        useHotKeyScope(HOT_KEY_SCOPE_NAME);

        const { onItemsRendered } = useSendDwh({
            storage,
            isSearchActive,
            isLoaded,
            isError: Boolean(error),
            items,
            isPopup,
        });

        useHotkeyHandlers({
            folder,
            selectedIdxs,
            storage,
            dispatch,
            items,
            handleOnClick,
            viewMode,
            isPublicUpload,
            selectedItems,
            hasAddedItem,
            listRef,
        });

        useEffect(() => {
            if (folder && lastOpenFolder && folder.id !== lastOpenFolder && lastOpenFolder.includes(folder.id)) {
                if (folder.id === '/') {
                    dispatch(scrollToItemAction(`/${lastOpenFolder.split('/')[1]}`));
                } else if (lastOpenFolder.split(folder.id).length > 2) {
                    dispatch(scrollToItemAction(lastOpenFolder.split(folder.id)[1]));
                } else {
                    dispatch(scrollToItemAction(lastOpenFolder));
                }

                dispatch(setLastOpenFolder(folder.id));
            }
        }, [currentRoute]);

        useEffect(() => {
            if (redirectFromViewerItem) {
                dispatch(scrollToItemAction(redirectFromViewerItem));
            }
        }, [redirectFromViewerItem]);

        const [isScrolledToTop, setIsScrolledToTop] = useState(true);
        const [scrollTopBtnClicked, setScrollTopBtnClicked] = useState(false);

        const showScrollTopInGallery = isGallery && !isFastScrollbar;
        const showScrollTopButton = isScrollTopBtnEnabled && (isHome || isPublic || showScrollTopInGallery || isAlbums);
        const canShowSharedFileAuthor = showSharedFileAuthor && isSharedCategory;

        const handleScrollTopBtnClicked = useCallback(() => {
            setScrollTopBtnClicked(true);
        }, [setScrollTopBtnClicked]);

        const renderItem = (
            id: string,
            options: { index: number; thumbType?: string },
            { onItemSizeChange }: { onItemSizeChange?: (size: number) => void } = {}
        ): ReactElement => (
            <WrapDataListItem
                id={id}
                isShowAiTooltip={isShowAiIconAndTooltip}
                isShowAiPhotoImprovingTooltip={
                    isShowAiPhotoImprovingTooltip && mediaFileTypes.some((extansion) => id.endsWith(`.${extansion}`))
                }
                optionIndex={options.index}
                view={viewMode}
                handleClickCreateNew={handleClickCreateNew}
                isSharedLinks={isSharedLinks}
                selectedIdxs={selectedIdxs}
                selectingIdxs={selectingIdxs}
                activeIndex={activeIndex}
                isDragging={isDragging}
                dragOverItemId={dragOverItem?.id}
                onSelect={handleOnSelect}
                onClick={handleOnClick}
                onContextMenu={handleOnContextMenuItem}
                onAuthorContextMenuItem={handleOnAuthorContextMenuItem}
                selectedCount={selectedCount}
                canShowSharedFileAuthor={canShowSharedFileAuthor}
                storage={storage || EStorageType.home}
                tipData={selectedIdxs[0] === id ? tipData : null}
                isFavoritesEnabled={isFavoritesEnabled}
                listOfBannedToFavoriteItems={listOfBannedToFavoriteItems}
                onDraggingOver={handleDraggingOver}
                onMouseLeave={handleMouseLeave}
                register={registerItem.current}
                unregister={unregisterItem.current}
                onMouseDown={handleMouseDownOnItem}
                rootRef={itemCreateNewRef}
                thumbType={options.thumbType}
                isPopup={isPopup}
                isPublicDownloaded={isPublicDownloaded}
                currentAllDocsType={currentAllDocsType}
                albumShowSelected={albumShowSelected}
                itemsAmount={itemsWithNewElementAndNoOtherFake}
                isSharedAutodelete={isSharedAutodelete}
                onItemSizeChange={onItemSizeChange}
                hideUpload={isDomainAndCreateRestricted}
                hideRestore={hideRestore}
                LastFilesWidget={LastFilesWidget}
                DataList={DataList}
            />
        );

        const categoryTypeForEmpty = useMemo(() => {
            if (isFeed) {
                return feedCategory;
            }

            if (isGallery) {
                return galleryCategory;
            }

            return attachType;
        }, [attachType, feedCategory, galleryCategory, isFeed, isGallery]);

        if (error) {
            if (isSearchActive && isSearch) {
                return <ErrorSearch data-qa-datalist={storage} isPopup={isPopup} />;
            }

            return (
                <div className={styles.root} data-qa-datalist={storage}>
                    <ErrorRetry centered={!isPublic && !disableCenteredLoader} isPopup={isPopup} />
                </div>
            );
        }

        if (((isNil(isLoading) || isLoading) && !isLoaded && !items.length) || !isElementLoaded) {
            if (isQuotaCleaner) {
                return <DataListItemPlug />;
            }

            return (
                <div className={styles.root} data-qa-datalist={storage}>
                    <Loader centered={!isPublic && !disableCenteredLoader} />
                    <Footer storage={storage} isAbsolutePos={true} withMargins={true} />
                </div>
            );
        }

        if (!items?.length || (items.length === 1 && items.includes(ID_NEW_ITEM) && bizCurrentCategory)) {
            const searchType = isPopup && searchRequestType ? searchRequestType : routerParams?.searchType;
            const searchQuery = isPopup && searchRequestQuery ? searchRequestQuery : routerParams?.query || attachesParams?.query;

            if (
                !isPublic &&
                !isQuotaCleaner &&
                folder?.id &&
                // Не выводим для корня и разделов, только для папки
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                folder.parent &&
                canDisplayEmptyStubByRights &&
                emptyBizCategory
            ) {
                return (
                    <div
                        onContextMenu={handleOnContextMenu}
                        className={classNames(styles.root, styles.root_new_portal_header)}
                        data-qa-datalist={storage}
                    >
                        {showBreadcrumbs && storage === EStorageType.home && (
                            <BreadcrumbsContainer className={styles.emptyStateBreadcrumbs_new_portal_header} />
                        )}
                        <EmptyFolder folder={folder} />
                    </div>
                );
            }

            if (isQuotaCleaner) {
                return <QuotaCleanerPlug />;
            }

            if (isSearchActive) {
                return (
                    <EmptySearch
                        data-qa-datalist={storage}
                        onContextMenu={handleOnContextMenu}
                        searchType={searchType as ESearchOptionType}
                        searchText={searchQuery}
                    />
                );
            }

            if (isGallery && isPopup) {
                return <SelectFromCloudEmpty />;
            }

            const showUploaderpromoForAllDocs =
                isAllDocuments && (currentAllDocsType === EAllDocumentsType.presentation || currentAllDocsType === EAllDocumentsType.pdf);

            const showUploadProvocation =
                (isFeed || (isGallery && !isPopup && galleryCategory !== GalleryCategoryType.mobile)) && !isDomainAndCreateRestricted;

            const isUploadProvocationVisible = showUploadProvocation && provocationRef.current?.canShow();

            const emptyBlock = () => {
                if (!!storage && EStorageType.familyFolders === storage) {
                    return <FamilyContentEmpty />;
                }

                if (
                    !!storage &&
                    [
                        EStorageType.sharedLinks,
                        EStorageType.sharedIncoming,
                        EStorageType.feed,
                        EStorageType.favorites,
                        EStorageType.gallery,
                        EStorageType.albums,
                        EStorageType.attaches,
                        EStorageType.trashbin,
                        EStorageType.sharedAutodelete,
                        isB2bBizUser ? EStorageType.incomingPublic : null,
                        enableNewPersonalDocuments ? EStorageType.documents : null,
                        domainFoldersFilterActive ? EStorageType.home : null,
                    ].includes(storage)
                ) {
                    const fullHeight = isB2bBizUser && ![EStorageType.feed].includes(storage);
                    return (
                        <>
                            <Empty.New storage={storage} categoryType={categoryTypeForEmpty} fullHeight={fullHeight} />
                            {showUploadProvocation && <UploadProvocation ref={provocationRef} maxHeight={152} itemsCount={items.length} />}
                        </>
                    );
                }

                return (
                    <Empty
                        searchType={galleryCategory || null}
                        type={isGallery ? 'gallery' : 'folder'}
                        storage={storage as EStorageType}
                        canUpload={isHome && !isReadOnlyFolder && !domainFoldersFilterActive}
                        weblink={getPublicUrl(folder)}
                        id={folder?.id}
                        canCoshare={!isDomainAndShareRestricted}
                        currentAllDocsType={currentAllDocsType}
                        currentAllDocsCategory={currentAllDocsCategory}
                        isNewTrashbin={currentStorage === EStorageType.alldocuments && storage === EStorageType.trashbin}
                    />
                );
            };

            return (
                <div
                    onContextMenu={handleOnContextMenu}
                    className={classNames(styles.root, styles.emptyRoot)}
                    data-qa-datalist={storage}
                    data-qa-view={viewMode}
                >
                    {showBreadcrumbs && (isPublic || (isHome && isReadOnlyFolder)) && (
                        <BreadcrumbsContainer
                            className={classNames(styles.emptyStateBreadcrumbs_new_portal_header, {
                                [styles.noMargin]: isPublicUpload,
                                [styles.noLeftPadding]: isB2bBizUser,
                            })}
                        />
                    )}

                    {isPublicUpload && (
                        <div className={styles.publicUploadHeader}>
                            <PublicUploadHeader />
                        </div>
                    )}
                    {emptyBlock()}
                    {showUploaderpromoForAllDocs && <ExtraBlocks itemsCount={items.length} hideUpload={isDomainAndCreateRestricted} />}
                    {currentStorage !== EStorageType.alldocuments && (
                        <Footer
                            bottom={isUploadProvocationVisible ? 3 : undefined}
                            storage={storage}
                            isAbsolutePos={!isUploadProvocationVisible}
                            withMargins={!isUploadProvocationVisible}
                        />
                    )}
                </div>
            );
        }

        if (hasExtraBlocksItem) {
            items = [...items, DATA_LIST_EXTRABLOCKS_ID];
        }

        if (isPublicUpload) {
            items = [DATA_LIST_PUBLIC_UPLOAD_HEADER_ID, ...items];
        }

        if (hasBreadCrumbsItem) {
            items = [DATA_LIST_BREADCRUMBS_ID, ...items];
        }

        if (isGallery && !isPopup) {
            items = [DATA_LIST_GALLERY_TAB_MENU_ID, ...items];
        }

        if (hasStoriesWidgetItem) {
            items = [DATA_LIST_STORIES_WIDGET_ID, ...items];
        }

        if (hasLastFilesWidgetItem) {
            items = [DATA_LIST_LAST_FILES_WIDGET_ID, ...items];
        }

        return (
            <>
                {showScrollTopButton && (
                    <ScrollTopButton
                        bottomOffset={isUploadButtonVisible ? 110 : 30}
                        scrollToTop={handleScrollTopBtnClicked}
                        isScrolledToTop={isScrolledToTop}
                        storage={storage}
                        isAlbum={isAlbums || publicFolder?.type === 'album'}
                        parentFolder={folder && 'parent' in folder && folder.parent}
                    />
                )}
                <SelectableGroup
                    tolerance={20}
                    onSelection={handleSelecting}
                    onBeginSelection={handleBeginSelection}
                    preventDefault={false}
                    fixedPosition={true}
                    enabled={selectBoxEnabled}
                    onClick={handleOnSelectGroupClick}
                    onEndSelection={handleSelectboxSelection}
                    selectboxClassName={styles.selectbox}
                    disableClickOnItem
                    className={classNames(styles.selectable, selectableClassName, {
                        [styles.selectable_withinDialog]: Boolean(withinDialog),
                        [styles.selectable_stories]: Boolean(storiesWidget),
                    })}
                >
                    {({ register, unregister }) => {
                        registerItem.current = register;
                        unregisterItem.current = unregister;

                        return (
                            <div
                                className={classNames(styles.root, styles.root_new_portal_header)}
                                onContextMenu={handleOnContextMenu}
                                onMouseLeave={handleMouseLeave}
                                data-qa-datalist={storage}
                                data-qa-view={viewMode}
                            >
                                <VirtualListNew
                                    hasMoreToLoad={disableLoadOnScroll ? false : hasMoreToLoad}
                                    list={items}
                                    viewMode={viewMode}
                                    renderItem={renderItem}
                                    rowHeight={ROW_HEIGHT}
                                    isLoading={isLoading}
                                    loadOnScroll={loadOnScroll}
                                    loadOnAnchor={loadOnAnchor}
                                    onItemsRendered={onItemsRendered}
                                    ref={listRef}
                                    customScrollElement={customScrollElement}
                                    disableScroll={storiesWidget}
                                    lowOverscan={isUploading}
                                    scrollTopBtnClicked={scrollTopBtnClicked}
                                    setScrollTopBtnClicked={setScrollTopBtnClicked}
                                    setIsScrolledToTop={setIsScrolledToTop}
                                />

                                {isDragging &&
                                    selectedCount > 0 &&
                                    selectedItems[0] &&
                                    !!dragPosX &&
                                    !!dragPosY &&
                                    createPortal(
                                        <DataListDragItem
                                            dragPosX={dragPosX}
                                            dragPosY={dragPosY}
                                            selectedCount={selectedCount}
                                            item={selectedItems[0]}
                                            thumb={getThumbUrl(selectedItems[0], isVirusItem(selectedItems[0]))}
                                        />,
                                        draggingPortalTarget
                                    )}
                            </div>
                        );
                    }}
                </SelectableGroup>
            </>
        );
    }
);

DataList.displayName = 'DataList';

DataList.whyDidYouRender = true;
